import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Webhook, Plus, Trash2, TestTube, Activity, Copy, Eye, EyeOff, Loader2, AlertCircle, CheckCircle2 } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Checkbox } from '@/components/ui/checkbox';
import { Switch } from '@/components/ui/switch';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { useAuthStore } from '@/stores/authStore';

interface Webhook {
  id: string;
  name: string;
  url: string;
  secret: string;
  events: string[];
  is_active: boolean;
  last_triggered_at: string | null;
  created_at: string;
}

interface WebhookDelivery {
  id: string;
  event_type: string;
  status_code: number | null;
  error_message: string | null;
  delivered_at: string;
}

export function WebhooksSection() {
  const { user } = useAuthStore();
  const [webhooks, setWebhooks] = useState<Webhook[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showLogsDialog, setShowLogsDialog] = useState(false);
  const [selectedWebhook, setSelectedWebhook] = useState<Webhook | null>(null);
  const [deliveries, setDeliveries] = useState<WebhookDelivery[]>([]);
  const [loadingLogs, setLoadingLogs] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [showSecret, setShowSecret] = useState<Record<string, boolean>>({});
  const [testingWebhook, setTestingWebhook] = useState<string | null>(null);
  
  const [newWebhook, setNewWebhook] = useState({
    name: '',
    url: '',
    events: ['qr.scanned'] as string[]
  });

  const availableEvents = [
    { id: 'qr.scanned', label: 'QR Code Scanned', description: 'Triggered when a QR code is scanned' },
    { id: 'qr.created', label: 'QR Code Created', description: 'Triggered when a new QR code is created' },
    { id: 'qr.updated', label: 'QR Code Updated', description: 'Triggered when a QR code is updated' },
    { id: 'qr.deleted', label: 'QR Code Deleted', description: 'Triggered when a QR code is deleted' }
  ];

  useEffect(() => {
    loadWebhooks();
  }, []);

  const loadWebhooks = async () => {
    try {
      const { data, error } = await supabase
        .from('webhooks')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;
      setWebhooks(data || []);
    } catch (error) {
      console.error('Failed to load webhooks:', error);
      toast.error('Failed to load webhooks');
    } finally {
      setIsLoading(false);
    }
  };

  const loadDeliveries = async (webhookId: string) => {
    setLoadingLogs(true);
    try {
      const { data, error } = await supabase
        .from('webhook_deliveries')
        .select('*')
        .eq('webhook_id', webhookId)
        .order('delivered_at', { ascending: false })
        .limit(50);

      if (error) throw error;
      setDeliveries(data || []);
    } catch (error) {
      console.error('Failed to load deliveries:', error);
      toast.error('Failed to load delivery logs');
    } finally {
      setLoadingLogs(false);
    }
  };

  const createWebhook = async () => {
    if (!newWebhook.name.trim() || !newWebhook.url.trim()) {
      toast.error('Please fill in all required fields');
      return;
    }

    if (!newWebhook.url.startsWith('http://') && !newWebhook.url.startsWith('https://')) {
      toast.error('URL must start with http:// or https://');
      return;
    }

    setIsCreating(true);
    try {
      const { data: secretData } = await supabase.rpc('generate_webhook_secret');
      const secret = secretData || `whsec_${Math.random().toString(36).substring(2)}`;

      const { error } = await supabase
        .from('webhooks')
        .insert({
          user_id: user?.id!,
          name: newWebhook.name,
          url: newWebhook.url,
          secret: secret,
          events: newWebhook.events
        });

      if (error) throw error;

      await loadWebhooks();
      setShowCreateDialog(false);
      setNewWebhook({ name: '', url: '', events: ['qr.scanned'] });
      toast.success('Webhook created successfully');
    } catch (error: any) {
      console.error('Failed to create webhook:', error);
      toast.error(error.message || 'Failed to create webhook');
    } finally {
      setIsCreating(false);
    }
  };

  const toggleWebhook = async (id: string, currentState: boolean) => {
    try {
      const { error } = await supabase
        .from('webhooks')
        .update({ is_active: !currentState })
        .eq('id', id);

      if (error) throw error;

      await loadWebhooks();
      toast.success(`Webhook ${!currentState ? 'enabled' : 'disabled'}`);
    } catch (error) {
      console.error('Failed to toggle webhook:', error);
      toast.error('Failed to update webhook');
    }
  };

  const deleteWebhook = async (id: string, name: string) => {
    if (!confirm(`Are you sure you want to delete the webhook "${name}"?`)) {
      return;
    }

    try {
      const { error } = await supabase
        .from('webhooks')
        .delete()
        .eq('id', id);

      if (error) throw error;

      await loadWebhooks();
      toast.success('Webhook deleted');
    } catch (error) {
      console.error('Failed to delete webhook:', error);
      toast.error('Failed to delete webhook');
    }
  };

  const testWebhook = async (webhook: Webhook) => {
    setTestingWebhook(webhook.id);
    try {
      const { error } = await supabase.rpc('trigger_webhook', {
        p_user_id: user?.id,
        p_event_type: 'webhook.test',
        p_payload: {
          test: true,
          message: 'This is a test webhook delivery from NexQR',
          timestamp: new Date().toISOString()
        }
      });

      if (error) throw error;

      // Trigger processor
      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
      const supabaseKey = import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY;
      
      await fetch(`${supabaseUrl}/functions/v1/process-webhooks`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${supabaseKey}`
        }
      });

      toast.success('Test webhook sent! Check your endpoint or delivery logs.');
    } catch (error) {
      console.error('Test failed:', error);
      toast.error('Failed to send test webhook');
    } finally {
      setTestingWebhook(null);
    }
  };

  const copyToClipboard = async (text: string, label: string) => {
    await navigator.clipboard.writeText(text);
    toast.success(`${label} copied`);
  };

  const toggleEventSelection = (eventId: string) => {
    setNewWebhook(prev => ({
      ...prev,
      events: prev.events.includes(eventId)
        ? prev.events.filter(e => e !== eventId)
        : [...prev.events, eventId]
    }));
  };

  const openLogs = (webhook: Webhook) => {
    setSelectedWebhook(webhook);
    loadDeliveries(webhook.id);
    setShowLogsDialog(true);
  };

  if (isLoading) {
    return (
      <Card>
        <CardContent className="pt-6">
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <>
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="space-y-6"
      >
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Webhook className="h-5 w-5" />
              Webhooks
            </CardTitle>
            <CardDescription>
              Receive real-time HTTP callbacks when events happen in your account
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium">Your Webhooks ({webhooks.length})</p>
                <p className="text-xs text-muted-foreground">
                  Automate workflows with Zapier, Make.com, or custom endpoints
                </p>
              </div>
              <Button onClick={() => setShowCreateDialog(true)}>
                <Plus className="h-4 w-4 mr-2" />
                Create Webhook
              </Button>
            </div>

            {webhooks.length === 0 ? (
              <div className="text-center py-12 border-2 border-dashed rounded-lg">
                <Webhook className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                <h3 className="font-medium text-lg mb-2">No Webhooks Yet</h3>
                <p className="text-sm text-muted-foreground mb-4">
                  Create your first webhook to start automating workflows
                </p>
                <Button onClick={() => setShowCreateDialog(true)}>
                  <Plus className="h-4 w-4 mr-2" />
                  Create Your First Webhook
                </Button>
              </div>
            ) : (
              <div className="space-y-3">
                {webhooks.map((webhook) => (
                  <Card key={webhook.id} className="border">
                    <CardContent className="pt-6">
                      <div className="flex items-start justify-between">
                        <div className="space-y-3 flex-1">
                          <div className="flex items-center gap-3">
                            <h4 className="font-medium">{webhook.name}</h4>
                            <Badge variant={webhook.is_active ? 'default' : 'secondary'}>
                              {webhook.is_active ? 'Active' : 'Paused'}
                            </Badge>
                          </div>

                          <div className="space-y-2">
                            <div className="flex items-center gap-2 text-sm">
                              <span className="text-muted-foreground font-mono truncate max-w-md">
                                {webhook.url}
                              </span>
                              <Button
                                variant="ghost"
                                size="sm"
                                className="h-6 px-2"
                                onClick={() => copyToClipboard(webhook.url, 'URL')}
                              >
                                <Copy className="h-3 w-3" />
                              </Button>
                            </div>

                            <div className="flex items-center gap-2">
                              <span className="text-xs text-muted-foreground">Secret:</span>
                              <span className="font-mono text-xs">
                                {showSecret[webhook.id] ? webhook.secret : '••••••••••••'}
                              </span>
                              <Button
                                variant="ghost"
                                size="sm"
                                className="h-6 px-2"
                                onClick={() => setShowSecret(prev => ({ ...prev, [webhook.id]: !prev[webhook.id] }))}
                              >
                                {showSecret[webhook.id] ? <EyeOff className="h-3 w-3" /> : <Eye className="h-3 w-3" />}
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                className="h-6 px-2"
                                onClick={() => copyToClipboard(webhook.secret, 'Secret')}
                              >
                                <Copy className="h-3 w-3" />
                              </Button>
                            </div>
                          </div>

                          <div className="flex flex-wrap gap-2">
                            {webhook.events.map((event) => (
                              <Badge key={event} variant="outline" className="text-xs">
                                {event}
                              </Badge>
                            ))}
                          </div>

                          {webhook.last_triggered_at && (
                            <p className="text-xs text-muted-foreground">
                              Last triggered: {new Date(webhook.last_triggered_at).toLocaleString()}
                            </p>
                          )}

                          <div className="flex gap-2">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => testWebhook(webhook)}
                              disabled={testingWebhook === webhook.id}
                            >
                              {testingWebhook === webhook.id ? (
                                <Loader2 className="h-4 w-4 animate-spin" />
                              ) : (
                                <TestTube className="h-4 w-4" />
                              )}
                              <span className="ml-2">Test</span>
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => openLogs(webhook)}
                            >
                              <Activity className="h-4 w-4 mr-2" />
                              Logs
                            </Button>
                            <Switch
                              checked={webhook.is_active}
                              onCheckedChange={() => toggleWebhook(webhook.id, webhook.is_active)}
                            />
                          </div>
                        </div>

                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => deleteWebhook(webhook.id, webhook.name)}
                          className="text-destructive hover:text-destructive hover:bg-destructive/10"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Webhook Security</CardTitle>
            <CardDescription>Verify webhook authenticity</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="bg-muted/50 rounded-lg p-4 space-y-3">
              <h4 className="font-medium">Signature Verification</h4>
              <p className="text-sm text-muted-foreground">
                Each webhook includes an <code className="bg-background px-1 rounded">X-NexQR-Signature</code> header with an HMAC-SHA256 signature.
                Verify it using your webhook secret to ensure authenticity.
              </p>
              <pre className="bg-background p-3 rounded text-xs overflow-x-auto">
{`const crypto = require('crypto');

function verifyWebhook(payload, signature, secret) {
  const hmac = crypto.createHmac('sha256', secret);
  const digest = hmac.update(payload).digest('hex');
  return crypto.timingSafeEqual(
    Buffer.from(signature),
    Buffer.from(digest)
  );
}`}
              </pre>
            </div>
          </CardContent>
        </Card>
      </motion.div>

      {/* Create Webhook Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Create New Webhook</DialogTitle>
            <DialogDescription>
              Configure a webhook endpoint to receive real-time events
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div>
              <Label htmlFor="webhook-name">Webhook Name</Label>
              <Input
                id="webhook-name"
                placeholder="e.g., Zapier Integration"
                value={newWebhook.name}
                onChange={(e) => setNewWebhook(prev => ({ ...prev, name: e.target.value }))}
                className="mt-1"
              />
            </div>

            <div>
              <Label htmlFor="webhook-url">Endpoint URL</Label>
              <Input
                id="webhook-url"
                type="url"
                placeholder="https://your-app.com/webhooks/nexqr"
                value={newWebhook.url}
                onChange={(e) => setNewWebhook(prev => ({ ...prev, url: e.target.value }))}
                className="mt-1"
              />
              <p className="text-xs text-muted-foreground mt-1">
                Must be a public HTTPS endpoint that accepts POST requests
              </p>
            </div>

            <div>
              <Label>Events to Subscribe</Label>
              <div className="space-y-2 mt-2">
                {availableEvents.map((event) => (
                  <div key={event.id} className="flex items-start space-x-2">
                    <Checkbox
                      id={event.id}
                      checked={newWebhook.events.includes(event.id)}
                      onCheckedChange={() => toggleEventSelection(event.id)}
                    />
                    <div className="flex-1">
                      <label
                        htmlFor={event.id}
                        className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                      >
                        {event.label}
                      </label>
                      <p className="text-xs text-muted-foreground">{event.description}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateDialog(false)}>
              Cancel
            </Button>
            <Button onClick={createWebhook} disabled={isCreating}>
              {isCreating ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Creating...
                </>
              ) : (
                <>
                  <Webhook className="h-4 w-4 mr-2" />
                  Create Webhook
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delivery Logs Dialog */}
      <Dialog open={showLogsDialog} onOpenChange={setShowLogsDialog}>
        <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Webhook Delivery Logs</DialogTitle>
            <DialogDescription>
              {selectedWebhook?.name} - Last 50 deliveries
            </DialogDescription>
          </DialogHeader>

          {loadingLogs ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
            </div>
          ) : deliveries.length === 0 ? (
            <div className="text-center py-8">
              <Activity className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
              <p className="text-muted-foreground">No deliveries yet</p>
            </div>
          ) : (
            <div className="space-y-2">
              {deliveries.map((delivery) => (
                <div
                  key={delivery.id}
                  className="border rounded-lg p-3 space-y-1"
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Badge variant="outline">{delivery.event_type}</Badge>
                      {delivery.status_code !== null && (
                        delivery.status_code >= 200 && delivery.status_code < 300 ? (
                          <CheckCircle2 className="h-4 w-4 text-green-500" />
                        ) : (
                          <AlertCircle className="h-4 w-4 text-red-500" />
                        )
                      )}
                      {delivery.status_code !== null && (
                        <Badge variant={
                          delivery.status_code >= 200 && delivery.status_code < 300
                            ? 'default'
                            : 'destructive'
                        }>
                          {delivery.status_code}
                        </Badge>
                      )}
                    </div>
                    <span className="text-xs text-muted-foreground">
                      {new Date(delivery.delivered_at).toLocaleString()}
                    </span>
                  </div>
                  {delivery.error_message && (
                    <p className="text-xs text-destructive">{delivery.error_message}</p>
                  )}
                </div>
              ))}
            </div>
          )}

          <DialogFooter>
            <Button onClick={() => setShowLogsDialog(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
